/**
 * VersionHistory — slide-over panel showing all saved versions.
 * Allows restore + preview per version.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, RotateCcw, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useEditorStore } from '../stores/editorStore';
import { API_URL } from '../../../api/api';

import { confirmDialog } from "../../../components/em/confirm";
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return 'just now';
}

export function VersionHistory() {
  const { newsletterId, versionPanelOpen, toggleVersionPanel, versions, setVersions, init } = useEditorStore();
  const [loading, setLoading]   = useState(false);
  const [restoring, setRestoring] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!versionPanelOpen || !newsletterId) return;
    setLoading(true);
    axios.get(`${API_URL}/api/newsletters/${newsletterId}/versions`, { headers })
      .then((r) => setVersions(r.data))
      .catch(() => toast.error('Could not load versions'))
      .finally(() => setLoading(false));
  }, [versionPanelOpen, newsletterId]); // eslint-disable-line

  const handleRestore = async (versionId) => {
    if (!(await confirmDialog('Restore this version? Current content will be overwritten.'))) return;
    setRestoring(versionId);
    try {
      const snap = await axios.post(
        `${API_URL}/api/newsletters/${newsletterId}/versions/${versionId}/restore`,
        {}, { headers }
      );
      // Re-init the editor with the restored snapshot
      init({ id: newsletterId, ...snap.data });
      toast.success('Version restored');
      toggleVersionPanel();
    } catch {
      toast.error('Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  if (!versionPanelOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 320,
      background: 'var(--em-bg-card)', borderLeft: '1px solid var(--em-border)',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
      zIndex: 1000, display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--em-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} color="#f54b01"/>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Version History</h3>
        </div>
        <button onClick={toggleVersionPanel}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--em-fg5)' }}>
          <X size={18}/>
        </button>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading && (
          <p style={{ color: 'var(--em-fg5)', fontSize: 13, textAlign: 'center', paddingTop: 24 }}>Loading versions…</p>
        )}
        {!loading && versions.length === 0 && (
          <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', paddingTop: 24 }}>
            No versions saved yet.<br/>Versions are auto-created every 5 min.
          </p>
        )}
        {versions.map((v) => (
          <div key={v.id} style={{
            padding: '12px 14px', borderRadius: 8, marginBottom: 8,
            background: 'var(--em-bg-head)', border: '1px solid var(--em-border)',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{v.label || `v${v.version_number}`}</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>{timeAgo(v.created_at)}</p>
            </div>
            <button
              onClick={() => handleRestore(v.id)}
              disabled={restoring === v.id}
              style={{
                background: 'none', border: '1px solid var(--em-border)', borderRadius: 6,
                padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                color: restoring === v.id ? '#9ca3af' : '#374151',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <RotateCcw size={12}/> {restoring === v.id ? '…' : 'Restore'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
